import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { Component, Input, OnInit, ViewChild, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { NgxCurrencyInputMode } from 'ngx-currency';
import { Router } from '@angular/router';

import { EmployeeService } from '@pages/employee/services/employee.service';
import { PortalSettingsService } from '@core/services/portal-settings.service';
import { EmployeeInfoService } from '@core/services/employee-info.service';
import { UtilityService } from '@core/services/utility.service';
import { ReadOnlyPdfViewerComponent, FileInfo } from '@shared/component/read-only-pdf-viewer/read-only-pdf-viewer.component';
import { EmployeeInfo } from '@typeDefs/home/responses/home';
import { EmployeeApiService } from '@pages/employee/services/employee.api.service';
import { HomeApiService } from '@pages/home/services/home.api.service';

@Component({
  selector: 'app-confirmation-modal',
  templateUrl: './confirmation-modal.component.html',
  styleUrls: ['./confirmation-modal.component.css'],
  providers: [],
})
export class ConfirmationModalComponent implements OnInit {
  @Input() data: any;
  @Input() errorMsg: string;
  @ViewChild(ReadOnlyPdfViewerComponent) ReadOnlyPdfViewerComponent: ReadOnlyPdfViewerComponent;

  private readonly activeModal = inject(NgbActiveModal);
  private readonly employeeService = inject(EmployeeService);
  private readonly employeeApiService = inject(EmployeeApiService);
  private readonly utilityService = inject(UtilityService);
  private readonly router = inject(Router);
  private readonly homeApiService = inject(HomeApiService);
  private readonly document = inject(DOCUMENT);

  private format: string = this.utilityService.getDateFormat().toUpperCase();
  private portalSettings = inject(PortalSettingsService).getPortalSettings();
  private employeeInfo: EmployeeInfo = inject(EmployeeInfoService).getEmployeeInfo();
  mobileMode: boolean = this.utilityService.isMobileMode();

  private currencySymbol: string = this.utilityService.getCurrencySymbol(this.employeeInfo.PRCo);
  showExpenseCategory = this.portalSettings.ExShowExpenseCategory.ConfigValue == '1';
  private expensePostingType: string = this.portalSettings.ExpensePostingType.ConfigValue;
  private extenion: string;
  private isOpen: any[];
  private newFiles: any[];
  private attachmentList: any[];
  private printMode = 'NewWindow';
  private downloadURL: string | null = null;
  private fileIsNotViewable = true;

  expenseData: Record<string, any> = {};
  options: Record<string, any> = {
    align: 'left',
    prefix: this.currencySymbol,
    precision: 2,
    nullable: true,
    inputMode: NgxCurrencyInputMode.Natural,
  };
  formData: Record<string, any> = { selectedAttachment: {} };
  savedFiles: any[];
  bdatePickerOptions = {
    dateInputFormat: this.format.toUpperCase(),
    containerClass: 'theme-blue',
    showTodayButton: true,
    showClearButton: true,
    clearPosition: 'left',
    showWeekNumbers: false,
  };
  catList: any[] = [];
  attachmentData: FileInfo = null;

  ngOnInit(): void {
    this.getExpenseModel();
    this.getCatg();
  }

  getExpenseModel(): void {
    const requestParam = 'receiptID=' + this.data.newID + '&returnMode=E';
    this.employeeService.getExpenseReceipts(requestParam).then((response: any) => {
      this.expenseData = response[0];
      this.expenseData.ExpenseDate = new Date();
      this.expenseData.ReceiptItemID = this.data.receiptItemID;
      this.getAttachment();
    });
  }

  getCatg(): void {
    this.openModal();
    this.employeeApiService.getCategory().subscribe(response => {
      if (response != null) {
        this.catList = response.filter(val => val.IsActive);
      }
    });
    this.closeModal();
  }

  async getAttachment(): Promise<void> {
    this.savedFiles = [];
    this.newFiles = [];
    this.savedFiles = await this.employeeService.getLineAttachments(this.expenseData.ReceiptID, null);
    if (this.savedFiles && this.savedFiles.length > 0) {
      this.formData.selectedAttachment = this.savedFiles[0];
      this.getAttachmentData();
    } else {
      this.attachmentData = null;
    }
    this.attachmentList = [];
    this.savedFiles.forEach((val: { AttachmentFileName: string }) => {
      const tempExten = val.AttachmentFileName.slice(val.AttachmentFileName.lastIndexOf('.'), val.AttachmentFileName.length);
      if (
        tempExten.toLowerCase() == '.jpg' ||
        tempExten.toLowerCase() == '.jpeg' ||
        tempExten.toLowerCase() == '.png' ||
        tempExten.toLowerCase() == '.tif' ||
        tempExten.toLowerCase() == '.tiff'
      )
        this.attachmentList.push(val);
    });
  }

  getAttachmentData(): void {
    if (!this.utilityService.fileIsNotViewable(this.formData.selectedAttachment.AttachmentFileName)) {
      this.homeApiService.getPdfFile(this.formData.selectedAttachment.FileAttachmentID).subscribe(
        (response: Blob) => {
          this.attachmentData = {
            ID: this.formData.selectedAttachment.FileAttachmentID,
            Name: this.formData.selectedAttachment.AttachmentFileName,
            Data: response,
          };
        },
        error => {
          this.attachmentData = {
            ID: this.formData.selectedAttachment.FileAttachmentID,
            Name: this.formData.selectedAttachment.AttachmentFileName,
            Data: null,
          };
        }
      );
    } else {
      this.attachmentData = {
        ID: this.formData.selectedAttachment.FileAttachmentID,
        Name: this.formData.selectedAttachment.AttachmentFileName,
        Data: null,
      };
    }
  }

  async openEditExpenseReceipt(): Promise<void> {
    this.openModal();
    try {
      const response = await this.employeeService.insertUpdateReceipt(
        this.expenseData.ReceiptID,
        this.data.userKeyID,
        this.employeeInfo.PRCo,
        this.expenseData.ReceiptDescription,
        moment(this.expenseData.ExpenseDate, this.format.toUpperCase()).format('YYYY-MM-DD'),
        this.expenseData.ExpenseCatgID,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        this.expenseData.ReceiptTaxType != null ? parseInt(this.expenseData.ReceiptTaxType) : null,
        null,
        null,
        this.expenseData.Amount
      );

      await this.employeeService.insertUpdateReceiptItem(this.data.receiptItemID, response.ReceiptID, this.expenseData.Amount || 0);
      this.closeModal();
      this.activeModal.dismiss();
      this.router.navigate(['/employee/editexpensereceipt/'], {
        fragment: `receiptID/${response.ReceiptID}/proxyuserID/${this.data.userKeyID}`,
      });
    } catch (error) {
      this.errorMsg = error?.error?.error ?? 'An error has occured.';
    }
  }

  async cancelClicked(): Promise<void> {
    this.openModal();

    try {
      const response = await this.employeeService.insertUpdateReceipt(
        this.expenseData.ReceiptID,
        this.data.userKeyID,
        this.employeeInfo.PRCo,
        this.expenseData.ReceiptDescription,
        moment(this.expenseData.ExpenseDate, this.format.toUpperCase()).format('YYYY-MM-DD'),
        this.expenseData.ExpenseCatgID,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        this.expenseData.Amount
      );
      await this.employeeService.insertUpdateReceiptItem(this.data.receiptItemID, response.ReceiptID, this.expenseData.Amount || 0);
      this.activeModal.close('close');
      this.closeModal();
    } catch (error) {
      this.errorMsg = error?.error?.error ?? 'An error has occured.';
    }
  }

  openModal(): void {
    this.document.getElementById('modalShare').style.display = 'block';
    this.document.getElementById('fadeShare').style.display = 'block';
  }

  closeModal(): void {
    this.document.getElementById('modalShare').style.display = 'none';
    this.document.getElementById('fadeShare').style.display = 'none';
  }
}
